import { type KeyboardEvent, memo, type MouseEvent, useCallback } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import {
  INLINE_CHIP_BASE,
  INLINE_CHIP_ICON,
  INLINE_CHIP_IDLE,
  INLINE_CHIP_INTERACTIVE,
  INLINE_CHIP_SELECTED,
} from '@/lib/crm/components/badges/inline-chip-styles';
import { cx } from '@/utils/cx';

import type {
  TriggerButtonTarget,
  TriggerContextItem,
} from './ActionOverview.helpers';

/**
 * One trigger context chip in the proposal header's expanded row — a flat
 * inline-badge chip (type icon + bare label) that gets the sanctioned soft
 * brand tint while its record is open on the slideout stack.
 *
 * Presentational: the item arrives fully resolved (store-fresh label,
 * open-state) from the orchestrator (TriggerShortcutButtons), so this leaf
 * carries no store hooks. Extracted so its click/keydown handlers are stable
 * `useCallback`s (per the frontend stable-reference rules) and so it can stop
 * event propagation — defensive against the accordion's full-card overlay
 * trigger.
 *
 * The visible label is a bare noun ("Ada Lovelace"); the accessible name
 * keeps the action verb ("View Ada Lovelace") — a WCAG 2.5.3 label-in-name
 * superset of the visible text.
 */
interface TriggerContextChipProps {
  item: TriggerContextItem;
  onActivate: (target: TriggerButtonTarget) => void;
}

// Compare the fields that fully determine the render instead of item object
// identity: `buildTriggerContextItems()` (fed by `deriveTriggerButtons()`)
// recreates the item array on every trigger-data refetch even when nothing
// changed, so identity comparison would re-render every chip on each refetch.
// - key/label/icon/isOpen/target primitives cover everything painted;
// - `onActivate` must still trigger a re-render so clicks stay wired to
//   current behavior.
function areTriggerContextChipPropsEqual(
  prev: TriggerContextChipProps,
  next: TriggerContextChipProps
): boolean {
  return (
    prev.item.key === next.item.key &&
    prev.item.label === next.item.label &&
    prev.item.icon === next.item.icon &&
    prev.item.isOpen === next.item.isOpen &&
    prev.item.target.kind === next.item.target.kind &&
    (prev.item.target.kind === 'entity'
      ? prev.item.target.entityId ===
        (next.item.target as { entityId?: string }).entityId
      : prev.item.target.url === (next.item.target as { url?: string }).url) &&
    prev.onActivate === next.onActivate
  );
}

export const TriggerContextChip = memo(function TriggerContextChip({
  item,
  onActivate,
}: TriggerContextChipProps) {
  const { kind } = item.target;
  const targetId =
    item.target.kind === 'entity' ? item.target.entityId : item.target.url;

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onActivate(
        kind === 'entity'
          ? { kind, entityId: targetId }
          : { kind, url: targetId }
      );
    },
    [onActivate, kind, targetId]
  );

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Keep Enter/Space from bubbling to the card's disclosure toggle.
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    }
  }, []);

  const Icon = item.icon;

  return (
    <AriaButton
      aria-pressed={item.isOpen}
      aria-label={`View ${item.label}`}
      className={cx(
        INLINE_CHIP_BASE,
        INLINE_CHIP_INTERACTIVE,
        item.isOpen ? INLINE_CHIP_SELECTED : INLINE_CHIP_IDLE
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Icon
        aria-hidden='true'
        className={cx(
          INLINE_CHIP_ICON,
          item.isOpen ? 'text-utility-brand-700' : 'text-utility-gray-500'
        )}
      />
      <span className='truncate min-w-0'>{item.label}</span>
    </AriaButton>
  );
}, areTriggerContextChipPropsEqual);
